<template>
  <Listbox as="div" :class="[classes]" class="!border-none">
    <ListboxLabel
      v-if="label"
      class="block text-base font-medium text-gray-700 dark:text-dark-body-text bg-light-box-background dark:bg-dark-box-background"
      :for="name">
      {{ label }}
    </ListboxLabel>

    <div class="flex mt-1">
      <div class="relative flex items-stretch flex-grow focus-within:z-10">
        <span class="absolute inset-y-0 right-0 flex items-center pr-2" @click="!disabled ? search() : null">
          <ChevronDown class="w-5 h-5 text-gray-400" aria-hidden="true" />
        </span>
        <Group class="absolute left-2 w-5 h-5 top-2.5" :color="disabled ? '#9d9e9d' : '#9d9e9d'" />

        <input
          ref="searchInput"
          :value="selected_item ? selected_item.name : search_term"
          :disabled="disabled"
          autocomplete="off"
          :placeholder="placeholder ? translatedPlaceholder : $t('Search...')"
          :class="[
            disabled ? 'text-common-grey' : '',
            inputClasses,
            isBoxBackground
              ? 'bg-light-box-background dark:bg-dark-box-background'
              : 'bg-light-screen-background dark:bg-dark-screen-background',
            'appearance-none block w-full px-3  py-2  border-gray-300 rounded-lg  placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light '
          ]"
          class="block w-full pl-8 transition ease-in-out rounded-lg focus:ring-blue focus:border-blue-light placeholder-common-grey border-light-divider-border dark:border-dark-divider-border duration-side-menu-transition-duration text-light-body-text dark:text-dark-body-text"
          @blur="blur()"
          @click="search"
          @input="
            ($event) => {
              show_results = true
              search_term = $event.target.value
            }
          ">
      </div>
    </div>

    <transition
      v-show="show_results"
      leave-active-class="transition duration-100 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0">
      <ListboxOptions
        static
        :class="[
          isBoxBackground
            ? 'bg-light-box-background dark:bg-dark-box-background'
            : 'bg-light-screen-background dark:bg-dark-screen-background',
          show_results ? 'ring-1 ring-black ring-opacity-5' : '',
          adjustHeight ? 'top-[49px]' : 'top-[59px]'
        ]"
        class="absolute inset-0 z-30 w-full overflow-y-auto text-base bg-white rounded-md shadow-lg h-[300px] !max-h-[300px] focus:outline-none sm:text-sm">
        <ListboxOption v-if="selected_item">
          <div class="flex justify-between">
            <div class="relative py-2 pl-2 pr-4 transition ease-in-out rounded-lg group duration-side-menu-transition-duration" />
            <div
              class="relative my-2 transition-all duration-300 ease-out cursor-pointer text-common-grey top-2 right-2 hover:text-dark-brand-blue"
              @click="reset">
              {{ $t("Clear") }}
            </div>
          </div>
        </ListboxOption>
        <ListboxOption
          v-if="search_results.length == 0 && search_term"
          class="flex items-center justify-center pt-2 my-2 text-center text-common-grey">
          {{ $t("No groups matching search query") }}
        </ListboxOption>
        <ListboxOption
          v-else-if="search_results.length == 0"
          class="mt-auto flex items-center justify-center pt-2 my-2 text-center text-common-grey">
          {{ $t("No groups to display") }}
        </ListboxOption>
        <ListboxOption v-show="search_results.length == 0" v-slot="{}" key="createGroup" class="flex items-center justify-center">
          <div
            class="m-2 btn primary-btn"
            @click="
              () => {
                $emitter.emit('createNewGroup', objectType)
                show_results = false
              }
            ">
            <div class="flex flex-nowrap">
              <Plus class="w-5 h-5 mr-2 text-white" />
              {{ $t("Create New Group") }}
            </div>
          </div>
        </ListboxOption>

        <ListboxOption v-if="selected_item" :value="selected_item" @click="handleGroupClick(null)">
          <div
            class="relative py-2 pl-4 pr-4 transition ease-in-out rounded-lg cursor-pointer group/name hover:bg-light-brand-blue dark:hover:bg-dark-brand-blue duration-side-menu-transition-duration"
            @mouseenter="group_item_hover = selected_item.id"
            @mouseleave="group_item_hover = null">
            <span
              :class="['text-light-body-text dark:text-dark-body-text group-hover/name:text-dark-body-text']"
              class="flex font-bold truncate transition ease-in-out duration-side-menu-transition-duration">
              <Check class="mr-2 w-5 h-5 mt-0.5 font-extrabold" /> {{ selected_item.name }}
            </span>
            <Pencil
              v-if="selected_item?.id"
              :color="pencil_hover == selected_item.id ? '#2DA7D7' : 'white'"
              class="absolute w-6 h-6 p-1 transition-all duration-300 ease-out rounded-md opacity-0 right-8 hover:bg-white top-1 group-hover/name:opacity-100 hover:text-light-brand-blue"
              @click.stop="handleUpdateGroup(selected_item)"
              @mouseenter="pencil_hover = selected_item.id"
              @mouseleave="pencil_hover = null" />
            <Trash2
              v-if="selected_item?.id"
              class="absolute transition-all ease-out duration-300 right-2 top-[5px] h-6 w-6 opacity-0 text-white group-hover/name:opacity-100 hover:bg-white hover:text-dark-brand-blue rounded-md p-1"
              @click.stop="handleDeleteGroup(selected_item)" />
          </div>
        </ListboxOption>

        <ListboxOption v-for="item in search_results"
                       v-slot="{}"
                       :key="item"
                       :value="item"
                       @click="handleGroupClick(item)">
          <div
            v-if="selected_item?.id != item.id"
            :class="''"
            class="relative py-2 pl-4 pr-4 transition ease-in-out rounded-lg cursor-pointer group/item hover:bg-light-brand-blue dark:hover:bg-dark-brand-blue duration-side-menu-transition-duration"
            @mouseenter="group_item_hover = item.id"
            @mouseleave="group_item_hover = null">
            <span
              :class="['text-light-body-text dark:text-dark-body-text group-hover/item:text-dark-body-text']"
              class="block truncate transition ease-in-out duration-side-menu-transition-duration">
              <CheckCheck
                class="inline mr-2 transition-all duration-300 ease-out w-4 h-4"
                :color="group_item_hover == item.id ? 'white' : '#899399'" />
              {{ item.name }}
              <Pencil
                v-if="item?.id"
                :color="pencil_hover == item.id ? '#2DA7D7' : 'white'"
                class="absolute w-6 h-6 p-1 transition-all duration-300 ease-out rounded-md opacity-0 right-8 hover:bg-white top-1 group-hover/item:opacity-100 hover:text-light-brand-blue"
                @click.stop="handleUpdateGroup(item)"
                @mouseenter="pencil_hover = item.id"
                @mouseleave="pencil_hover = null" />
              <Trash2
                v-if="item?.id"
                class="absolute transition-all ease-out duration-300 right-2 top-[5px] h-6 w-6 opacity-0 text-white group-hover/item:opacity-100 hover:bg-white hover:text-dark-brand-blue rounded-md p-1"
                @click.stop="handleDeleteGroup(item)" />
            </span>
          </div>
        </ListboxOption>
      </ListboxOptions>
    </transition>
  </Listbox>
</template>

<script>
import { Listbox, ListboxLabel, ListboxOption, ListboxOptions } from "@headlessui/vue"
import { Group, ChevronDown, Check, Plus, Trash2, Pencil, CheckCheck } from "lucide-vue-next"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"

export default {
  components: {
    Plus,
    Listbox,
    ListboxLabel,
    ListboxOptions,
    ListboxOption,
    ChevronDown,
    Check,
    Group,
    CheckCheck,
    Pencil,
    Trash2
  },
  props: {
    adjustHeight: {
      type: Boolean,
      default: false
    },
    items: {
      type: Array,
      required: true
    },

    label: {
      type: String,
      required: false
    },

    placeholder: {
      type: String,
      required: false,
      default: "Search"
    },
    inputClasses: {
      type: String,
      default: "text"
    },
    classes: {
      type: String,
      default: "text"
    },
    disabled: {
      type: Boolean,
      default: false
    },

    isBoxBackground: {
      type: Boolean,
      default: false
    },
    objectType: {
      type: String,
      default: "company"
    },
    focusTrigger: {
      type: Boolean,
      default: false
    },
    resetTrigger: {
      type: Number,
      required: false
    },
    returnValue: {
      type: Boolean,
      default: true
    },
    currentItem: {
      type: [Object, String],
      required: false
    }
  },
  emits: ["update:currentItem", "groupSelection"],
  data() {
    return {
      show_results: false,
      search_term: "",
      search_results: [],
      selected_item: null,
      pencil_hover: null,
      group_item_hover: null
    }
  },
  computed: {
    translatedPlaceholder() {
      return this.$t(this.placeholder)
    }
  },
  watch: {
    currentItem() {
      this.selected_item = this.currentItem
    },
    resetTrigger() {
      this.reset()
    },
    search_term() {
      if (this.search_term != null && this.show_results) {
        this.search_results = this.items.filter((item) => {
          return item.name.toLowerCase().includes(this.search_term.toLowerCase())
        })
      } else {
        this.search_results = this.items
      }
    },

    focusTrigger() {
      setTimeout(() => {
        this.$refs.searchInput?.focus()
        this.$refs.searchInput?.click()
      }, 200)
    }
  },

  created() {
    if (this.currentItem) {
      this.selected_item = this.currentItem
    }
    this.register_listeners()
  },
  activated() {
    this.register_listeners()
  },
  unmounted() {
    this.unregister_listeners()
  },
  deactivated() {
    this.unregister_listeners()
  },

  methods: {
    register_listeners() {
      this.$emitter.on(ALL_EMITTERS.GROUP_UPDATED_IN_GROUP_DROPDOWN, (group) => {
        if (group.id == this.selected_item?.id) {
          this.selected_item = group
        }
      })
    },
    unregister_listeners() {
      this.$emitter.off(ALL_EMITTERS.GROUP_UPDATED_IN_GROUP_DROPDOWN)
    },
    handleDeleteGroup(group) {
      this.$emitter.emit(ALL_EMITTERS.DELETE_GROUP, { group: group })
    },
    handleUpdateGroup(group) {
      this.$emitter.emit(ALL_EMITTERS.EDIT_GROUP, { group: group, filter: group.filter })
    },

    handleGroupClick(item) {
      this.selected_item = item
      this.$emit("groupSelection", this.selected_item.id)
      this.show_results = false
      this.$emit("update:currentItem", this.returnValue ? this.selected_item : this.selected_item.id)
    },
    search() {
      setTimeout(() => {
        this.search_results = this.items
        this.$refs.searchInput.focus()
        this.show_results = true
      }, 200)
    },
    blur() {
      setTimeout(() => {
        this.show_results = false
      }, 50)
    },
    reset() {
      this.selected_item = null
      this.show_results = false
      this.search_term = ""
      this.$emit("update:currentItem", this.selected_item)
    }
  }
}
</script>

<style scoped>
.mh-48 {
  max-height: 10rem;
}
</style>
